import React, { useState, useEffect, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Select from 'react-select'
import { AuthContext } from '../context/AuthContext'
import { ThemeContext } from '../context/ThemeContext'
import BackButton from './BackButton'
import '../styles/Leaderboard.css'
import config from '../config'
import {
  formatCurrency as fc,
  getImageUrl,
  LoadingSpinner,
  customStylesTrans,
} from '../helper/StylesHelper'
import {
  categoryOptions,
  timeOptions,
  mobileTimeOptions,
} from '../helper/FeedHelper'

// updated sortOptions
import {
  handleDeleteLeaderboard,
  handleToggleVerificationLeaderboard,
  sortOptions,
  LeaderboardCardUser, // Renders each user item
} from '../helper/LeaderboardHelper'
import { getChangePercentageColor } from '../helper/MarketsHelper'
import CreateLeaderboardModal from './CreateLeaderboardModal'
import VerifiedIcon from './VerifiedIcon'
import useResponsiveWidth from '../hooks/useResponsiveWidth'
import CreateTournamentModal from './CreateTournamentModal'
import TournamentModal from './TournamentModal'

// Import a custom hook "useDebounce" (assumed existing at ../hooks/useDebounce)
import { useDebounce } from '../hooks/useDebounce'

export default function Leaderboard() {
  useResponsiveWidth()
  const { username } = useParams()
  const navigate = useNavigate()

  const {
    userToken,
    isAuthenticated,
    isAdmin,
    userId,
    username: currentUsername,
  } = useContext(AuthContext)
  const { tc } = useContext(ThemeContext)

  // Basic LB details
  const [leaderboardDetails, setLeaderboardDetails] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Investors (left side) filters
  const [selectedLeftTime, setSelectedLeftTime] = useState(timeOptions[0].value)
  const [selectedLeftSortOption, setSelectedLeftSortOption] = useState(
    sortOptions[0]
  )

  // Subgroups (right side) filters
  const [selectedRightTime, setSelectedRightTime] = useState(
    timeOptions[0].value
  )
  const [selectedRightSortOption, setSelectedRightSortOption] = useState(
    sortOptions[0]
  )

  // Members data & pagination
  const [leaderboardMembers, setLeaderboardMembers] = useState([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersPage, setMembersPage] = useState(1)
  const [membersTotal, setMembersTotal] = useState(0)

  // Subgroups data & pagination
  const [subgroupsList, setSubgroupsList] = useState([])
  const [subgroupsLoading, setSubgroupsLoading] = useState(false)
  const [subgroupsPage, setSubgroupsPage] = useState(1)
  const [subgroupsTotal, setSubgroupsTotal] = useState(0)

  // CREATE SUBGROUP MODAL
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editId, setEditId] = useState(null)
  const [parentLeaderboardDetails, setParentLeaderboardDetails] = useState(null)

  // Flag if this LB has a parent
  const isSubgroup = leaderboardDetails?.parentLeaderboard

  // ======== DEBOUNCED SEARCH STATES ========
  const [searchMembers, setSearchMembers] = useState('') // for the investors search
  const debouncedSearchMembers = useDebounce(searchMembers, 500)

  const [searchSubgroups, setSearchSubgroups] = useState('') // for subgroups search
  const debouncedSearchSubgroups = useDebounce(searchSubgroups, 500)
  const [showTournamentPopup, setShowTournamentPopup] = useState(false)
  const [showCreateTournamentPopup, setShowCreateTournamentPopup] =
    useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [activeSection, setActiveSection] = useState('investors')

  const toggleSection = (section) => {
    setActiveSection(section)
  }

  // ========== Effects ==========
  useEffect(() => {
    const fetchLeaderboardDetails = async () => {
      try {
        setIsLoading(true)
        const response = await axios.get(
          `${config.backendUrl}/api/leaderboards/${username}`,
          {
            headers: { Authorization: `Bearer ${userToken}` },
          }
        )

        setLeaderboardDetails(response.data)
        if (response.data.parentLeaderboard) {
          fetchSingleParentLb(response.data.parentLeaderboard)
        }
      } catch (error) {
        console.error('Error fetching leaderboard details:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchLeaderboardDetails()
  }, [username, userToken, navigate])

  // whenever left-time or left-sort changes, or we update search, reset page
  useEffect(() => {
    setMembersPage(1)
    setLeaderboardMembers([])
  }, [selectedLeftTime, selectedLeftSortOption, debouncedSearchMembers])

  // fetch members
  useEffect(() => {
    if (!leaderboardDetails?._id) return

    const fetchLeaderboardMembers = async () => {
      try {
        setMembersLoading(true)
        const res = await axios.get(
          `${config.backendUrl}/api/leaderboards/${leaderboardDetails.username}/members`,
          {
            params: {
              timePeriod: selectedLeftTime,
              sortBy: selectedLeftSortOption.value,
              asc: selectedLeftSortOption.asc,
              page: membersPage,
              limit: 20,
              searchQuery: debouncedSearchMembers,
            },
            headers: { Authorization: `Bearer ${userToken}` },
          }
        )

        const data = res.data

        // store user rank
        setLeaderboardDetails((prev) => {
          if (!prev) return null
          return {
            ...prev,
            userRank: data.userRank,
            userPercentile: data.userPercentile,
          }
        })

        if (membersPage === 1) {
          setLeaderboardMembers(data.members || [])
        } else {
          setLeaderboardMembers((prev) => [...prev, ...(data.members || [])])
        }
        setMembersTotal(data.totalResults || 0)
      } catch (err) {
        console.error('Error fetching leaderboard members:', err)
      } finally {
        setMembersLoading(false)
      }
    }

    fetchLeaderboardMembers()
  }, [
    leaderboardDetails?._id,
    selectedLeftTime,
    selectedLeftSortOption,
    membersPage,
    userToken,
    debouncedSearchMembers,
  ])

  // whenever right-time or right-sort changes, or search subgroups changes, reset subgroups page
  useEffect(() => {
    setSubgroupsPage(1)
    setSubgroupsList([])
  }, [selectedRightTime, selectedRightSortOption, debouncedSearchSubgroups])

  // fetch subgroups
  useEffect(() => {
    if (!leaderboardDetails?._id) return

    async function fetchSubgroups() {
      try {
        setSubgroupsLoading(true)
        const resp = await axios.get(
          `${config.LBbackendUrl}/api/leaderboards/top-leaderboards`,
          {
            params: {
              timePeriod: selectedRightTime,
              sortBy: selectedRightSortOption.value,
              asc: selectedRightSortOption.asc,
              parentLeaderboard: leaderboardDetails._id,
              page: subgroupsPage,
              limit: 20,
              searchQuery: debouncedSearchSubgroups,
            },
          }
        )
        const data = resp.data || {}

        if (subgroupsPage === 1) {
          setSubgroupsList(data.topLeaderboards || [])
        } else {
          setSubgroupsList((prev) => [...prev, ...(data.topLeaderboards || [])])
        }
        setSubgroupsTotal(data.totalResults || 0)
      } catch (error) {
        console.error('Error fetching subgroups:', error)
      } finally {
        setSubgroupsLoading(false)
      }
    }

    fetchSubgroups()
  }, [
    leaderboardDetails?._id,
    selectedRightTime,
    selectedRightSortOption,
    subgroupsPage,
    debouncedSearchSubgroups,
  ])

  // ========== Join / Leave ==========
  const handleJoin = async () => {
    if (!leaderboardDetails) return
    try {
      await axios.post(
        `${config.backendUrl}/api/leaderboards/${leaderboardDetails._id}/join`,
        {},
        { headers: { Authorization: `Bearer ${userToken}` } }
      )
      setLeaderboardDetails((prev) => {
        if (!prev) return null
        return { ...prev, totalMembers: prev.totalMembers + 1, isMember: true }
      })
    } catch (err) {
      console.error('Error joining leaderboard:', err)
      alert(err?.response?.data?.error || 'Error joining leaderboard')
    }
  }

  // Fetch the "parent" LB if we only have its ID and want to prepopulate
  async function fetchSingleParentLb(lbId) {
    try {
      const resp = await axios.get(
        `${config.backendUrl}/api/leaderboards/${lbId}`,
        {
          headers: { Authorization: `Bearer ${userToken}` },
        }
      )
      const parentData = resp.data
      if (!parentData || !parentData._id) return
      // We have a valid parent LB:
      setParentLeaderboardDetails(parentData)
    } catch (err) {
      console.error('Error fetching parent LB by ID:', err)
      // if it fails, it's not critical
    }
  }

  const handleLeave = async () => {
    if (!leaderboardDetails) return
    try {
      await axios.post(
        `${config.backendUrl}/api/leaderboards/${leaderboardDetails._id}/leave`,
        {},
        { headers: { Authorization: `Bearer ${userToken}` } }
      )
      setLeaderboardDetails((prev) => {
        if (!prev) return null
        return {
          ...prev,
          totalMembers: Math.max(prev.totalMembers - 1, 0),
          isMember: false,
        }
      })
    } catch (err) {
      console.error('Error leaving leaderboard:', err)
      alert(err?.response?.data?.error || 'Error leaving leaderboard')
    }
  }

  const handleLockUnlockLeaderboard = async () => {
    if (!leaderboardDetails) return

    if (
      !window.confirm(
        `Are you sure you want to ${
          leaderboardDetails.isLocked ? 'unlock' : 'lock'
        } this leaderboard? If locked, no new members can join.`
      )
    ) {
      return
    }

    try {
      // Send request to backend toggling lock
      const response = await axios.put(
        `${config.backendUrl}/api/leaderboards/${leaderboardDetails._id}/toggle-lock`,
        {}, // no special request body, just toggling
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      )

      // response.data might have { message, isLocked } or similar
      alert(response.data.message)

      // Optionally update local state with new locked state
      setLeaderboardDetails((prev) =>
        prev
          ? {
              ...prev,
              // flip or set the new isLocked if the backend returns a boolean
              isLocked: response.data.isLocked,
            }
          : prev
      )
    } catch (error) {
      console.error('Error toggling leaderboard lock:', error)
      // Show some user-friendly message
      alert(error.response?.data?.error || 'Error toggling leaderboard lock')
    }
  }

  // “View More”
  const handleViewMoreMembers = () => {
    setMembersPage((prev) => prev + 1)
  }

  const handleViewMoreSubgroups = () => {
    setSubgroupsPage((prev) => prev + 1)
  }

  // ========== CREATE SUBGROUP ==========
  const handleCreateSubgroup = () => {
    if (!isAuthenticated) {
      alert('Please sign in to create a subgroup.')
      return
    }
    // Also check membership
    if (!leaderboardDetails?.isMember) {
      alert('You must join the leaderboard to create a subgroup.')
      return
    }
    if (!leaderboardDetails) return
    setIsEditMode(false)
    setEditId(null)
    setShowCreateModal(true)
  }

  const handleCloseCreateModal = () => {
    setShowCreateModal(false)
    setIsEditMode(false)
    setEditId(null)
  }

  const handleViewTournaments = () => {
    setShowTournamentPopup(true)
  }

  const handleTournamentClick = () => {
    setShowTournamentPopup(true)
  }

  // ========== Render ==========

  if (isLoading) {
    return (
      <div className="sLeadContainer">
        <BackButton padding="2rem 0" />
        <div className="sLoadingSpinnerWrapper">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (!leaderboardDetails) {
    return null
  }

  const {
    name,
    bio,
    totalMembers,
    profileImage,
    isMember,
    userRank,
    userPercentile,
  } = leaderboardDetails

  const canViewMoreMembers = leaderboardMembers.length < membersTotal
  const canViewMoreSubgroups = subgroupsList.length < subgroupsTotal

  // -- Render Subgroup Card --
  function renderSubgroupCard(subgroup, idx) {
    const rank = idx + 1
    const valueToUse =
      selectedRightSortOption.value === '%return'
        ? subgroup.percentageGain
        : subgroup.netGain
    const arrow = valueToUse >= 0 ? '↑' : '↓'
    const textColor =
      rank === 1 ? '#FFC328' : getChangePercentageColor(valueToUse, tc())

    return (
      <div
        className="LeadRightSideUserCard"
        key={subgroup.leaderboardId || subgroup._id}
        onClick={() => navigate(`/leaderboard/${subgroup.username}`)}
      >
        <span
          className="LeadRightSideUserCardRank"
          style={{
            color: rank === 1 ? textColor : 'var(--text-color)',
            marginRight: '10px',
          }}
        >
          {rank}
        </span>

        <div
          className="LeadRightSideUserImageWrapper"
          style={{
            boxShadow:
              rank === 1 ? '0 0 0.6rem #030902, 0 0 2rem #FFBF19' : 'none',
          }}
        >
          <img
            src={getImageUrl(subgroup.profileImage)}
            alt="subgroup"
            className="LeadRightSideUserImg"
          />
        </div>

        <div className="LeadRightSideUserDetails">
          <div
            style={{ color: rank === 1 ? textColor : 'var(--text-color)' }}
            className="LeadRightSideUserName bold"
          >
            <span className="bold">
              {subgroup.name}{' '}
              {subgroup.isLeaderboardVerified ? (
                <VerifiedIcon
                  fontSize="1.3rem"
                  color={rank === 1 ? textColor : 'var(--stock-change-pos)'}
                />
              ) : (
                ''
              )}
            </span>
          </div>
          <div
            className="LeadRightSideLargestPosition bold"
            style={{ color: textColor, flexDirection: 'row' }}
          >
            {arrow}
            {selectedRightSortOption.value === 'netReturn' && '$'}
            {fc(Math.abs(valueToUse), 2, true)}
            {selectedRightSortOption.value === '%return' && '%'}{' '}
            <span style={{ color: 'var(--text-color)' }}>
              {selectedRightTime?.toLowerCase()}
            </span>
          </div>
          <span
            className="bold"
            style={{
              color: 'var(--action-grey)',
              fontSize: 'var(--font-med-l)',
            }}
          >
            {subgroup.totalMembers} member
            {subgroup.totalMembers !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="sLeadContainer">
      <BackButton padding="2rem 0" />
      <div className={`LeadBackdrop ${showCreateModal ? 'active' : ''}`} />
      <div className={`LeadBackdrop ${showTournamentPopup ? 'active' : ''}`} />
      {leaderboardDetails && (
        <CreateLeaderboardModal
          isOpen={showCreateModal}
          onClose={handleCloseCreateModal}
          isEditMode={isEditMode}
          editId={editId}
          userToken={userToken}
          parentLbId={leaderboardDetails._id}
          parentLbUsername={leaderboardDetails.username}
        />
      )}
      {showTournamentPopup && (
        <TournamentModal
          leaderboardId={leaderboardDetails._id}
          currentLeader={leaderboardMembers[0]?.user.name || ''}
          percentageGain={leaderboardMembers[0]?.percentageGain || 0}
          leaderboardCreator={leaderboardDetails.creator}
          onClose={() => setShowTournamentPopup(false)}
        />
      )}

      {showCreateTournamentPopup && (
        <CreateTournamentModal
          leaderboardId={leaderboardDetails._id}
          onClose={() => {
            setShowCreateTournamentPopup(false)
          }}
        />
      )}
      {/* LEADERBOARD HEADER */}
      <div className="sLeaderboardDetailsWrapper">
        <div className="sLeaderboardHeader">
          <div className="sLeaderboardInfo">
            <h1 className="sLeaderboardName">
              {name}{' '}
              {leaderboardDetails?.isLeaderboardVerified ? (
                <VerifiedIcon />
              ) : (
                ''
              )}
            </h1>
            <p className="sLeaderboardUsername medium">
              v/{username}
              {leaderboardDetails && (
                <span className="medium">
                  {' - created by '}
                  <span
                    onClick={() =>
                      navigate(`/profile/${leaderboardDetails?.creator}`)
                    }
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    className="bold"
                  >
                    {leaderboardDetails?.creator}
                  </span>
                  {` on ${new Date(
                    leaderboardDetails?.createdAt
                  ).toLocaleDateString()}`}
                </span>
              )}
              {isSubgroup && (
                <p className="sLeaderboardStats">
                  Parent Leaderboard:{' '}
                  <span
                    onClick={() =>
                      navigate(
                        `/leaderboard/${parentLeaderboardDetails?.username}`
                      )
                    }
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    className="bold"
                  >
                    v/{parentLeaderboardDetails?.username}
                  </span>
                </p>
              )}
            </p>

            <div
              className="sLeaderboardActions"
              style={{ marginBottom: '2rem', marginTop: '2rem' }}
            >
              {isMember ? (
                <button className="CreatePostFeedButton" onClick={handleLeave}>
                  Leave
                </button>
              ) : (
                <button
                  className="CreatePostFeedButton greenButton"
                  onClick={handleJoin}
                >
                  Join
                </button>
              )}
              <button
                className="CreatePostFeedButton"
                onClick={handleCreateSubgroup}
                style={{
                  backgroundColor: isMember
                    ? 'var(--stock-change-pos)'
                    : 'var(--action-grey)',

                  color: isMember ? '#000' : 'var(--text-color)',
                }}
              >
                Create Subgroup
              </button>
              {!isMobile && (
                <>
                  <span className="separator">-</span>
                  <button
                    className="CreatePostFeedButton tournament-button"
                    onClick={handleTournamentClick}
                    style={{
                      border: '1.5px solid #ffbf19',
                      color: '#FFBF19',
                      fontWeight: 'bold',
                    }}
                  >
                    Tournament Details
                  </button>
                </>
              )}
              {isAdmin && (
                <>
                  <button
                    className="CreatePostFeedButton"
                    onClick={() =>
                      handleToggleVerificationLeaderboard(
                        leaderboardDetails?._id,
                        userToken,
                        (resData) => {
                          const leaderboard = resData.leaderboard
                          const updatedLeaderboard = {
                            ...leaderboardDetails,
                            isLeaderboardVerified:
                              leaderboard.isLeaderboardVerified,
                          }
                          setLeaderboardDetails(updatedLeaderboard)
                        }
                      )
                    }
                  >
                    {leaderboardDetails?.isLeaderboardVerified
                      ? 'Unverify'
                      : 'Verify'}
                  </button>

                  <button
                    onClick={() =>
                      handleDeleteLeaderboard(
                        leaderboardDetails?._id,
                        userToken
                      )
                    }
                    className="CreatePostFeedButton"
                  >
                    Delete
                  </button>
                </>
              )}

              {(isAdmin || currentUsername === leaderboardDetails.creator) && (
                <button
                  onClick={handleLockUnlockLeaderboard}
                  className="CreatePostFeedButton"
                >
                  {leaderboardDetails.isLocked ? 'Unlock' : 'Lock'}
                </button>
              )}
            </div>
            {bio && (
              <>
                <div className="pr-stat-label-wrapper">
                  <span className="pr-stat-label bold">Bio</span>
                  <span className="pr-bio">{bio}</span>
                </div>
              </>
            )}
            <div className="pr-stats" style={{ margin: '2rem 0 0 0' }}>
              <div className="pr-stat-label-wrapper">
                <span className="pr-stat-label bold">Subgroups</span>
                <span className="bold">
                  {leaderboardDetails?.subgroupsCount}
                </span>
              </div>
              <div className="pr-stat-label-wrapper">
                <span className="pr-stat-label bold">Members</span>
                <span className="bold">{fc(totalMembers, 0)}</span>
              </div>

              <div className="pr-stat-label-wrapper">
                <span className="pr-stat-label bold">All time</span>
                <span
                  className="bold"
                  style={{
                    color: getChangePercentageColor(
                      leaderboardDetails?.latestSnapshot
                        ?.allTimePercentageReturn,
                      tc()
                    ),
                  }}
                >
                  {leaderboardDetails?.latestSnapshot
                    ?.allTimePercentageReturn >= 0
                    ? '↑'
                    : '↓'}
                  &nbsp;
                  {fc(
                    leaderboardDetails?.latestSnapshot?.allTimePercentageReturn,
                    2,
                    true
                  )}
                  %
                </span>
              </div>

              <div className="pr-stat-label-wrapper">
                <span className="pr-stat-label bold">Leaderboard Ranking</span>
                <span className="bold">#{leaderboardDetails?.allTimeRank}</span>
              </div>
            </div>
          </div>
          <div className="sLeaderboardProfileImage">
            {profileImage && (
              <img
                src={getImageUrl(profileImage)}
                alt={`${name} profile`}
                className="sProfileImage"
              />
            )}
          </div>
        </div>
      </div>
      {isMobile ? (
        <>
          <div
            className="ProfileContentWrapper"
            style={{ borderTop: 'var(--border)' }}
          >
            <div className="ProfileToggleButtonContainer">
              <button
                className={`CreatePostFeedButton ${
                  activeSection === 'investors' ? 'greenButton' : ''
                }`}
                onClick={() => toggleSection('investors')}
              >
                Investors
              </button>
              <button
                className={`CreatePostFeedButton ${
                  activeSection === 'subgroups' ? 'greenButton' : ''
                }`}
                onClick={() => toggleSection('subgroups')}
              >
                Subgroups
              </button>
            </div>
            <div
              className={`ProfilePortfolioContent ${
                activeSection === 'investors' ? 'active' : ''
              }`}
            >
              <div className="sLeaderboardLeftContent">
                <div className="sLeaderboardLeftContentHeader">
                  <h2 className="sLeaderboardName" style={{ margin: 0 }}>
                    Investors
                  </h2>
                  <span className="sLeaderboardPosition">
                    Your position:
                    {leaderboardDetails.userRank
                      ? ` ${leaderboardDetails.userRank} (top ${fc(
                          leaderboardDetails.userPercentile,
                          2
                        )}%)`
                      : ' N/A'}
                  </span>
                </div>

                {/* Instead of a time <Select>, use row of buttons */}
                <div className="LeadBestButtonsWrapper">
                  <div className="LeadBestButtons">
                    {mobileTimeOptions.map((t) => (
                      <button
                        key={t.value}
                        className="CreatePostFeedButton"
                        style={{
                          backgroundColor:
                            selectedLeftTime === t.value
                              ? 'var(--stock-change-pos)'
                              : 'var(--background-color)',
                          color:
                            selectedLeftTime === t.value
                              ? '#000'
                              : 'var(--text-color)',
                        }}
                        onClick={() => {
                          setSelectedLeftTime(t.value)
                          setMembersPage(1)
                          setLeaderboardMembers([])
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {/* <span className="separator">-</span> */}
                  {/* Show Tournament Button if there are tournaments */}
                  {/* <button
                    className="CreatePostFeedButton tournament-button"
                    style={{
                      border: '1.5px solid #ffbf19',
                      color: '#FFBF19',
                      fontWeight: 'bold',
                    }}
                    onClick={handleViewTournaments}
                  >
                    Tournament
                  </button> */}
                </div>

                {/* Sort by (Select) & search bar row */}
                <div className="sLeaderboardSelects">
                  <Select
                    value={selectedLeftSortOption}
                    onChange={(option) => {
                      setMembersPage(1)
                      setLeaderboardMembers([])
                      setSelectedLeftSortOption(option)
                    }}
                    options={sortOptions}
                    isSearchable={false}
                    placeholder="Best % Returns"
                    unstyled
                    styles={customStylesTrans(tc())}
                  />

                  {/* Debounced search for members */}
                  <input
                    style={{ width: '50%', marginLeft: 'auto' }}
                    className="FeedFiltersModalSearch"
                    type="text"
                    placeholder="Search investors"
                    value={searchMembers}
                    onChange={(e) => {
                      setSearchMembers(e.target.value)
                      setMembersPage(1)
                      setLeaderboardMembers([])
                    }}
                  />
                </div>

                {/* Investors list */}
                <div className="InvestorLeaderboardCardUser">
                  {membersLoading && membersPage === 1 ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      {leaderboardMembers.map((member, idx) => (
                        <LeaderboardCardUser
                          key={idx}
                          topPortfolio={member}
                          index={idx}
                          selectedSortOption={selectedLeftSortOption.value}
                          navigate={navigate}
                          tc={tc}
                          currentUserId={userId} // the logged in user's ID
                          userToken={userToken}
                        />
                      ))}
                      {!membersLoading && leaderboardMembers.length === 0 && (
                        <p style={{ marginTop: '1rem' }}>No members found.</p>
                      )}
                    </>
                  )}
                  {membersLoading && membersPage > 1 && <LoadingSpinner />}
                </div>

                {!membersLoading && canViewMoreMembers && (
                  <button
                    className="CreatePostFeedButton"
                    style={{ marginTop: '1rem' }}
                    onClick={handleViewMoreMembers}
                  >
                    View More
                  </button>
                )}
              </div>
            </div>
            <div
              className={`ProfileMainContent ${
                activeSection === 'subgroups' ? 'active' : ''
              }`}
            >
              <div className="sLeaderboardRightContent">
                <h2 className="sLeaderboardName" style={{ margin: 0 }}>
                  Subgroups
                </h2>

                <div className="LeadBestButtonsWrapper">
                  <div className="LeadBestButtons">
                    {mobileTimeOptions.map((t) => (
                      <button
                        key={t.value}
                        className="CreatePostFeedButton"
                        style={{
                          backgroundColor:
                            selectedRightTime === t.value
                              ? 'var(--stock-change-pos)'
                              : 'var(--background-color)',
                          color:
                            selectedRightTime === t.value
                              ? '#000'
                              : 'var(--text-color)',
                        }}
                        onClick={() => {
                          setSubgroupsPage(1)
                          setSubgroupsList([])
                          setSelectedRightTime(t.value)
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {/* <span className="separator">-</span> */}
                  {/* Show Tournament Button if there are tournaments */}
                  {/* <button
                    className="CreatePostFeedButton tournament-button"
                    style={{
                      border: '1.5px solid #ffbf19',
                      color: '#FFBF19',
                      fontWeight: 'bold',
                    }}
                    onClick={handleViewTournaments}
                  >
                    Tournament
                  </button> */}
                </div>

                {/* Sort by (Select) & search bar row */}
                <div className="sLeaderboardSelects">
                  <Select
                    value={selectedRightSortOption}
                    onChange={(option) => {
                      setSubgroupsPage(1)
                      setSubgroupsList([])
                      setSelectedRightSortOption(option)
                    }}
                    options={sortOptions}
                    isSearchable={false}
                    placeholder="Best % Returns"
                    unstyled
                    styles={customStylesTrans(tc())}
                  />

                  {/* Debounced search for subgroups */}
                  <input
                    style={{ width: '50%', marginLeft: 'auto' }}
                    className="FeedFiltersModalSearch"
                    type="text"
                    placeholder="Search subgroups"
                    value={searchSubgroups}
                    onChange={(e) => {
                      setSearchSubgroups(e.target.value)
                      setSubgroupsPage(1)
                      setSubgroupsList([])
                    }}
                  />
                </div>

                <div
                  style={{
                    marginTop: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                  }}
                >
                  {subgroupsLoading && subgroupsPage === 1 ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      {subgroupsList.map((sg, i) => renderSubgroupCard(sg, i))}
                      {!subgroupsLoading && subgroupsList.length === 0 && (
                        <p>No subgroups found.</p>
                      )}
                    </>
                  )}
                  {subgroupsLoading && subgroupsPage > 1 && <LoadingSpinner />}

                  {canViewMoreSubgroups && !subgroupsLoading && (
                    <button
                      className="CreatePostFeedButton"
                      style={{ marginTop: '1rem' }}
                      onClick={handleViewMoreSubgroups}
                    >
                      View More
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
      {!isMobile && (
        <div className="sLeaderboardMainContent">
          {/* LEFT CONTENT (Investors) */}
          <div className="sLeaderboardLeftContent">
            <div className="sLeaderboardLeftContentHeader">
              <h2 className="sLeaderboardName" style={{ margin: 0 }}>
                Investors
              </h2>
              <span className="sLeaderboardPosition">
                Your position:
                {leaderboardDetails.userRank
                  ? ` ${leaderboardDetails.userRank} (top ${fc(
                      leaderboardDetails.userPercentile,
                      2
                    )}%)`
                  : ' N/A'}
              </span>
            </div>

            {/* Instead of a time <Select>, use row of buttons */}
            <div
              style={{
                marginTop: '1rem',
                marginBottom: '1rem',
                display: 'flex',
                gap: '1rem',
              }}
            >
              {timeOptions.map((t) => (
                <button
                  key={t.value}
                  className="CreatePostFeedButton"
                  style={{
                    backgroundColor:
                      selectedLeftTime === t.value
                        ? 'var(--stock-change-pos)'
                        : 'var(--background-color)',
                    color:
                      selectedLeftTime === t.value
                        ? '#000'
                        : 'var(--text-color)',
                  }}
                  onClick={() => {
                    setSelectedLeftTime(t.value)
                    setMembersPage(1)
                    setLeaderboardMembers([])
                  }}
                >
                  {t.label}
                </button>
              ))}
              <span className="separator">-</span>
              {/* Show Tournament Button if there are tournaments */}
              <button
                className="CreatePostFeedButton tournament-button"
                style={{
                  border: '1.5px solid #ffbf19',
                  color: '#FFBF19',
                  fontWeight: 'bold',
                }}
                onClick={handleViewTournaments}
              >
                Tournament
              </button>
            </div>

            {/* Sort by (Select) & search bar row */}
            <div className="sLeaderboardSelects">
              <Select
                value={selectedLeftSortOption}
                onChange={(option) => {
                  setMembersPage(1)
                  setLeaderboardMembers([])
                  setSelectedLeftSortOption(option)
                }}
                options={sortOptions}
                isSearchable={false}
                placeholder="Best % Returns"
                unstyled
                styles={customStylesTrans(tc())}
              />

              {/* Debounced search for members */}
              <input
                style={{ width: '25%', marginLeft: 'auto' }}
                className="FeedFiltersModalSearch"
                type="text"
                placeholder="Search investors"
                value={searchMembers}
                onChange={(e) => {
                  setSearchMembers(e.target.value)
                  setMembersPage(1)
                  setLeaderboardMembers([])
                }}
              />
            </div>

            {/* Investors list */}
            <div className="sLeaderboardUsersWrapper">
              {membersLoading && membersPage === 1 ? (
                <LoadingSpinner />
              ) : (
                <>
                  {leaderboardMembers.map((member, idx) => (
                    <LeaderboardCardUser
                      key={idx}
                      topPortfolio={member}
                      index={idx}
                      selectedSortOption={selectedLeftSortOption.value}
                      navigate={navigate}
                      tc={tc}
                      currentUserId={userId} // the logged in user's ID
                      userToken={userToken}
                    />
                  ))}
                  {!membersLoading && leaderboardMembers.length === 0 && (
                    <p style={{ marginTop: '1rem' }}>No members found.</p>
                  )}
                </>
              )}
              {membersLoading && membersPage > 1 && <LoadingSpinner />}
            </div>

            {!membersLoading && canViewMoreMembers && (
              <button
                className="CreatePostFeedButton"
                style={{ marginTop: '1rem' }}
                onClick={handleViewMoreMembers}
              >
                View More
              </button>
            )}
          </div>

          {/* RIGHT CONTENT (Subgroups) */}
          <div className="sLeaderboardRightContent">
            <h2 className="sLeaderboardName" style={{ margin: 0 }}>
              Subgroups
            </h2>

            <div
              style={{
                marginTop: '1rem',
                marginBottom: '1rem',
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              {timeOptions.map((t) => (
                <button
                  key={t.value}
                  className="CreatePostFeedButton"
                  style={{
                    backgroundColor:
                      selectedRightTime === t.value
                        ? 'var(--stock-change-pos)'
                        : 'var(--background-color)',
                    color:
                      selectedRightTime === t.value
                        ? '#000'
                        : 'var(--text-color)',
                  }}
                  onClick={() => {
                    setSubgroupsPage(1)
                    setSubgroupsList([])
                    setSelectedRightTime(t.value)
                  }}
                >
                  {t.label}
                </button>
              ))}
              {/* <span className="separator">-</span> */}
              {/* Show Tournament Button if there are tournaments */}
              {/* <button
                className="CreatePostFeedButton tournament-button"
                style={{
                  border: '1.5px solid #ffbf19',
                  color: '#FFBF19',
                  fontWeight: 'bold',
                }}
                onClick={handleViewTournaments}
              >
                Tournament
              </button> */}
            </div>

            {/* Sort by (Select) & search bar row */}
            <div className="sLeaderboardSelects">
              <Select
                value={selectedRightSortOption}
                onChange={(option) => {
                  setSubgroupsPage(1)
                  setSubgroupsList([])
                  setSelectedRightSortOption(option)
                }}
                options={sortOptions}
                isSearchable={false}
                placeholder="Best % Returns"
                unstyled
                styles={customStylesTrans(tc())}
              />

              {/* Debounced search for subgroups */}
              <input
                style={{ width: '50%', marginLeft: 'auto' }}
                className="FeedFiltersModalSearch"
                type="text"
                placeholder="Search subgroups"
                value={searchSubgroups}
                onChange={(e) => {
                  setSearchSubgroups(e.target.value)
                  setSubgroupsPage(1)
                  setSubgroupsList([])
                }}
              />
            </div>

            <div
              style={{
                marginTop: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
              }}
            >
              {subgroupsLoading && subgroupsPage === 1 ? (
                <LoadingSpinner />
              ) : (
                <>
                  {subgroupsList.map((sg, i) => renderSubgroupCard(sg, i))}
                  {!subgroupsLoading && subgroupsList.length === 0 && (
                    <p>No subgroups found.</p>
                  )}
                </>
              )}
              {subgroupsLoading && subgroupsPage > 1 && <LoadingSpinner />}

              {canViewMoreSubgroups && !subgroupsLoading && (
                <button
                  className="CreatePostFeedButton"
                  style={{ marginTop: '1rem' }}
                  onClick={handleViewMoreSubgroups}
                >
                  View More
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
